<script setup lang="ts">
type Props = {
  options?: any[];
  displayValue?: string;
  disabled?: boolean;
  hasIcon?: boolean;
};

const {
  options = [],
  displayValue = 'label',
  disabled,
  hasIcon,
} = defineProps<Props>();

const modelValue = defineModel<any>('modelValue', {
  default: '',
});

const slots: any = useSlots();

type Emits = {
  'prepend-option-click': [];
  'append-option-click': [];
};

const emit = defineEmits<Emits>();

function onPrependOptionClick() {
  emit('prepend-option-click');
}

function onAppendOptionClick() {
  emit('append-option-click');
}

const query = ref('');

const filteredOptions = computed(() =>
  query.value === ''
    ? options
    : options.filter((option) =>
        option[displayValue]
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(query.value.toLowerCase().replace(/\s+/g, '')),
      ),
);

watchEffect(() => {
  if (options[0]) {
    modelValue.value = options[0];
  }
});
</script>

<template>
  <HeadlessCombobox
    v-model="modelValue"
    :disabled="disabled"
  >
    <div :class="[$style.combobox, { [$style.isDisabled]: disabled }]">
      <HeadlessComboboxInput
        :class="$style.input"
        :disabled="disabled"
        :display-value="(option: any) => option[displayValue]"
        @change="query = $event.target.value"
      />

      <HeadlessComboboxButton
        :class="[$style.arrowButton, { [$style.isDisabled]: disabled }]"
      >
        <NuxtIcon
          :class="$style.arrowIcon"
          name="arrows/angle_down"
        />
      </HeadlessComboboxButton>

      <HeadlessTransitionRoot
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        @after-leave="query = ''"
      >
        <HeadlessComboboxOptions :class="$style.optionsList">
          <div
            v-if="filteredOptions.length === 0 && query !== ''"
            :class="$style.notFound"
          >
            Nenhuma opção encontrada.
          </div>

          <li
            v-if="slots['prepend-option']"
            :class="$style.option"
            @click.stop="onPrependOptionClick"
          >
            <slot name="prepend-option" />
          </li>

          <HeadlessComboboxOption
            v-for="(option, index) in filteredOptions"
            :key="index"
            v-slot="{ selected, active }"
            as="template"
            :value="option"
          >
            <li
              :class="[
                $style.option,
                {
                  'bg-teal-600 text-white': active,
                  'text-gray-900': !active,
                },
              ]"
            >
              <slot
                name="item"
                v-bind="{ option }"
              >
                <slot
                  v-if="hasIcon"
                  v-bind="{ option }"
                  name="icon"
                >
                  <NuxtIcon
                    :filled="true"
                    :name="`cryptos/${option[displayValue]}`"
                  />
                </slot>
                <span
                  class="block truncate"
                  :class="{
                    'font-medium': selected,
                    'font-normal': !selected,
                  }"
                >
                  {{ option[displayValue] }}
                </span>
              </slot>
            </li>
          </HeadlessComboboxOption>

          <li
            v-if="slots['append-option']"
            :class="$style.option"
            @click.stop="onAppendOptionClick"
          >
            <slot name="append-option" />
          </li>
        </HeadlessComboboxOptions>
      </HeadlessTransitionRoot>
    </div>
  </HeadlessCombobox>
</template>

<style module lang="postcss">
.combobox {
  @apply relative cursor-default w-full overflow-hidden rounded-lg bg-auth-bg-inputs text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-primary sm:text-sm;
}

.input {
  @apply w-full border-none py-2 pl-3 pr-10 text-sm leading-5 focus:ring-0 bg-auth-bg-inputs text-auth-text-inputs h-[46px] sm:h-[50px];

  &[disabled]:not([disabled='false']) {
    @apply cursor-not-allowed;
  }
}

.arrowButton {
  @apply absolute inset-y-0 right-0 flex items-center pr-2 text-auth-text-inputs;
}

.arrowIcon {
  @apply text-[.9em] opacity-70;
}

.optionsList {
  @apply fixed mt-1 max-h-48 overflow-auto rounded-md bg-auth-bg-inputs text-auth-text-inputs py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-50 cursor-pointer;
}

.notFound {
  @apply relative select-none px-4 py-2;
}

.option {
  @apply flex items-center gap-2 select-none py-2 px-4;
}

.isDisabled {
  @apply cursor-not-allowed;
}
</style>
